import { Component, ReactNode } from 'react'
import { Result, Button, Typography, Card } from 'antd'
import { ExclamationCircleOutlined, ReloadOutlined, HomeOutlined, MessageOutlined } from '@ant-design/icons'
import type { ErrorState } from '../types/milestone.types'
import { errorService } from '../services/errorService'

const { Paragraph, Text } = Typography

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State extends ErrorState {
  eventId?: string
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorMessage: undefined,
      errorCode: undefined,
      eventId: undefined
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorMessage: error.message,
      errorCode: error.name,
      eventId: 'error-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9)
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', {
      error,
      errorInfo,
      eventId: this.state.eventId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    })

    errorService.showError(
      'Application Error',
      error.message || 'An unexpected error occurred',
      [
        {
          code: error.name || 'REACT_ERROR',
          message: error.message,
          details: {
            eventId: this.state.eventId,
            componentStack: errorInfo.componentStack,
            url: window.location.href
          }
        }
      ]
    )
  }

  handleReport = () => {
    errorService.showError(
      'Application Error',
      this.state.errorMessage || 'An unexpected error occurred',
      [
        {
          code: this.state.errorCode || 'REACT_ERROR',
          message: this.state.errorMessage || 'Unknown error',
          details: {
            eventId: this.state.eventId,
            url: window.location.href
          }
        }
      ]
    )
  }

  handleReload = () => {
    window.location.reload()
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorMessage: undefined,
      errorCode: undefined,
      eventId: undefined
    })
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div style={{ 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f8ff 0%, #e6f2ff 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px'
        }}>
          <Card style={{ marginBottom: '24px', maxWidth: '600px', width: '100%' }}>
            <Result
              status="error"
              icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              title="Something went wrong"
              subTitle="An unexpected error occurred while loading the application. Our team has been notified."
              extra={[
                <Button 
                  key="retry" 
                  type="primary" 
                  icon={<ReloadOutlined />}
                  onClick={this.handleRetry}
                >
                  Try Again
                </Button>,
                <Button 
                  key="reload" 
                  icon={<ReloadOutlined />}
                  onClick={this.handleReload}
                >
                  Reload Page
                </Button>,
                <Button 
                  key="home" 
                  icon={<HomeOutlined />}
                  onClick={this.handleGoHome}
                >
                  Go Home
                </Button>,
                <Button 
                  key="report"
                  icon={<MessageOutlined />}
                  onClick={this.handleReport}
                >
                  Report to Rocket Pricing
                </Button>
              ]}
            >
              <div style={{ textAlign: 'left' }}>
                <Paragraph>
                  <Text strong>Error Details:</Text>
                </Paragraph>
                <Paragraph>
                  <Text code>{this.state.errorCode}: {this.state.errorMessage}</Text>
                </Paragraph>
                {this.state.eventId && (
                  <Paragraph>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Error ID: {this.state.eventId}
                    </Text>
                  </Paragraph>
                )}
                <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                  If this problem persists, please contact support with the error ID above.
                </Paragraph>
              </div>
            </Result>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
